import type {
  FieldMapping,
  FieldMappingFilter,
  FieldMappingInput,
  FieldMappingSeedResult,
} from '@/types/control/field-mapping';

import { controlApiRequest } from './control-api-client';

/**
 * Field mappings on control-api's admin surface.
 *
 * Two things about these five routes drive the whole shape of this file:
 *
 * 1. `tenant_id` is a **required query parameter on every route, including the
 *    writes**, where it is not a body field. Omitting it is a `400`, so every
 *    method here takes a tenant id and puts it in the query.
 * 2. The list route returns a **bare array**, not `{mappings, count}`. Control's
 *    old service declared the envelope, which has never existed on the wire, so
 *    its table was always empty.
 *
 * `GET /field-mappings/{id}`, `POST /field-mappings/batch` and
 * `POST /field-mappings/find` are deliberately absent: all three resolve onto
 * the `{id}` node, whose only methods are `PUT` and `DELETE`, so they answer
 * `405` with an empty body. They had no call site in Control either.
 */

const BASE = '/api/admin/field-mappings';

type FieldMappingResponse = {
  id?: unknown;
  tenant_id?: unknown;
  source_field?: unknown;
  normalized_field?: unknown;
  collector_type?: unknown;
  description?: unknown;
  is_active?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** `collector_type` and `description` are omitted rather than nulled when unset. */
function asOptional(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function toDomain(res: FieldMappingResponse): FieldMapping {
  return {
    id: asString(res.id),
    tenantId: asString(res.tenant_id),
    sourceField: asString(res.source_field),
    normalizedField: asString(res.normalized_field),
    collectorType: asOptional(res.collector_type),
    description: asOptional(res.description),
    isActive: res.is_active === true,
    createdAt: asString(res.created_at),
    updatedAt: asString(res.updated_at),
  };
}

/**
 * Optional fields are omitted rather than nulled, which relies on Control
 * treating PUT as a full replace: `FieldMappingsController.update` builds a
 * fresh `FieldMapping` from the request, so an omitted field lands as its zero
 * value and clearing a collector type in the edit modal works. If that handler
 * ever switched to a merge, an omitted field would mean "leave unchanged" and
 * clearing would silently stop working.
 */
function toWire(input: FieldMappingInput): Record<string, unknown> {
  return {
    source_field: input.sourceField,
    normalized_field: input.normalizedField,
    collector_type: input.collectorType ?? undefined,
    description: input.description ?? undefined,
    is_active: input.isActive,
  };
}

/**
 * Build the list query. An absent filter and an empty-string filter both mean
 * "do not filter", so blank inputs are dropped rather than sent — and
 * `is_active` is only sent when it is a real boolean, because control-api
 * answers `400` for a non-boolean.
 */
function listQuery(
  tenantId: string,
  filter: FieldMappingFilter | undefined
): Record<string, string | boolean> {
  const query: Record<string, string | boolean> = { tenant_id: tenantId };

  if (filter?.sourceField) {
    query.source_field = filter.sourceField;
  }
  if (filter?.normalizedField) {
    query.normalized_field = filter.normalizedField;
  }
  if (filter?.collectorType) {
    query.collector_type = filter.collectorType;
  }
  if (typeof filter?.isActive === 'boolean') {
    query.is_active = filter.isActive;
  }

  return query;
}

export const fieldMappingsService = {
  async list(
    tenantId: string,
    filter?: FieldMappingFilter,
    signal?: AbortSignal
  ): Promise<FieldMapping[]> {
    const res = await controlApiRequest<FieldMappingResponse[] | null>({
      path: BASE,
      query: listQuery(tenantId, filter),
      signal,
    });

    if (!Array.isArray(res)) {
      return [];
    }

    return res.map(toDomain);
  },

  async create(
    tenantId: string,
    input: FieldMappingInput
  ): Promise<FieldMapping> {
    const res = await controlApiRequest<FieldMappingResponse>({
      method: 'POST',
      path: BASE,
      query: { tenant_id: tenantId },
      body: toWire(input),
    });

    return toDomain(res);
  },

  /** The `{id}` in the path overwrites any id in the body, so none is sent. */
  async update(
    tenantId: string,
    id: string,
    input: FieldMappingInput
  ): Promise<FieldMapping> {
    const res = await controlApiRequest<FieldMappingResponse>({
      method: 'PUT',
      path: `${BASE}/${encodeURIComponent(id)}`,
      query: { tenant_id: tenantId },
      body: toWire(input),
    });

    return toDomain(res);
  },

  /** Answers `200 {status: "deleted"}` — a body, not a `204`. */
  async remove(tenantId: string, id: string): Promise<void> {
    await controlApiRequest<{ status?: string }>({
      method: 'DELETE',
      path: `${BASE}/${encodeURIComponent(id)}`,
      query: { tenant_id: tenantId },
    });
  },

  /**
   * Seed the tenant's baseline mappings.
   *
   * This had never actually run before control PR #1751: it was a no-op that
   * wrote nothing, while the old page claimed three times that defaults were
   * seeded automatically. No tenant has ever had them, so the page offers it
   * explicitly rather than implying it already happened.
   */
  async seedDefaults(tenantId: string): Promise<FieldMappingSeedResult> {
    const res = await controlApiRequest<{
      created?: unknown;
      total_defaults?: unknown;
    }>({
      method: 'POST',
      path: `${BASE}/seed`,
      query: { tenant_id: tenantId },
    });

    // A body without the counts is not a successful no-op. Reporting it as
    // "0 of 0 already present" would be an unfortunate echo of the bug this
    // ticket exists to fix — seeding that silently did nothing for years.
    if (
      typeof res?.created !== 'number' ||
      typeof res?.total_defaults !== 'number'
    ) {
      throw new Error(
        'Seeding returned an unexpected response, so it is unclear what was written.'
      );
    }

    return { created: res.created, totalDefaults: res.total_defaults };
  },
};
